import { ResponseApi } from "@store/generalTypes";
import { Instruction } from "../store/assessmentFormSlice";
import { WordResponseType, Word, WordCategory } from "../store/assessmentSlice";
import { ResultType } from "@utils/stt/useSpeechToText";

export const getInstructionFromStep = (instructions: Instruction[], stepName: string): Instruction => {
    const instruction = instructions.find((i) => i.stepName === stepName);

    return instruction ?? ({} as Instruction);
};

export const getWordsAndAlternatesFromList = (words: Word[]): string[] => {
    return words.reduce((prev: string[], curr) => prev.concat(curr.value, ...curr.alternates.slice(0, 3)), []);
};

export const getWordsAndAlternatesFromListCategorized = (words: Word[], category: WordCategory): string[] => {
    return words
        .filter((w) => w.category === category)
        .reduce((prev: string[], curr) => prev.concat(curr.value, ...curr.alternates.slice(0, 3)), []);
};

interface SanitizedWord {
    value: string;
    category: WordCategory;
    intrusion: boolean;
}

export interface CategorizedWords {
    [key: string]: SanitizedWord;
}

export const flattenAndCategorize = (
    categorizedWords: CategorizedWords,
    words: Word[],
    intrusion: boolean
): CategorizedWords => {
    const categorizedClone = JSON.parse(JSON.stringify(categorizedWords));

    words.forEach((word) => {
        categorizedClone[word.value] = {
            category: word.category,
            value: word.value,
            intrusion
        };

        word.alternates?.forEach((alt) => {
            categorizedClone[alt] = {
                category: word.category,
                value: word.value,
                intrusion
            };
        });
    });

    return categorizedClone;
};

export const filterAndMatch = (
    words: string[],
    categorizedWords: CategorizedWords,
    currentCategory?: WordCategory
): ResponseApi[] => {
    return words.reduce((filtered: ResponseApi[], word) => {
        word = word?.toLocaleLowerCase();

        if (!categorizedWords[word]) {
            return filtered.concat({
                response: word,
                responseType: WordResponseType.OutOfScope
            });
        }

        let isMatch = !categorizedWords[word].intrusion;

        if (currentCategory !== undefined) {
            isMatch = isMatch && categorizedWords[word].category === currentCategory;
        }

        const responseType: WordResponseType = isMatch ? WordResponseType.Correct : WordResponseType.Intrusion;

        return filtered.concat({
            response: categorizedWords[word].value,
            responseType
        });
    }, []);
};

export const getUniqueWords = (words: ResultType[]): string[][] => {
    return words.map((x) => [...x.transcript.split(" "), ...getCombinedWords(x.transcript.split(" "))]);
};

const getCombinedWords = (words: string[]): string[] => {
    const combinedWords = [];

    for (let i = 0; i < words.length - 1; i++) {
        const combined = words[i] + " " + words[i + 1];
        combinedWords.push(combined);
    }

    return combinedWords;
};
